/**
 * <RoleGate> — role-scoped UI wrapper for the operator personas.
 *
 * Foundation component for the role-scoped operations UI epic (#188).
 * Wraps any subtree that should only be visible to users holding
 * `required` or any role above it in the hierarchy
 * (`Admin > Engineer > SiteOperator > EventOperator`). Hierarchy logic
 * is centralised in `@jeket-cloud/types` via `canActAs` — do not reinvent.
 *
 * Defense-in-depth: this gate is *one* of three layers. The orchestrator
 * BFF (`requireRole` middleware, #216) and deployment-service must enforce
 * the same boundary on every mutating endpoint — `<RoleGate>` is a UX
 * convenience, not a security control.
 *
 * @example Plain gate
 *   <RoleGate role={Role.Engineer}>
 *     <Button onClick={execIntoPod}>Open Shell</Button>
 *   </RoleGate>
 *
 * @example With fallback
 *   <RoleGate role={Role.Admin} fallback={<DisabledHint reason="admin only" />}>
 *     <Button onClick={drainNode}>Drain Node</Button>
 *   </RoleGate>
 *
 * @example Explain mode
 *   Append `?explainGates=1` to the URL, or build with
 *   `VITE_EXPLAIN_ROLE_GATES=true` set, and any blocked gate renders a
 *   tooltip-on-hover stub explaining the required role to a developer or
 *   admin viewer. Unblocked gates pass through unchanged.
 */
import { useMemo, type ReactNode } from 'react';
// See AuthContext.tsx for the rationale — types package is CommonJS, so
// rollup's named-export analysis trips on `exports.X = X` patterns. Use
// the namespace import + destructure for runtime values, and `import
// type` for type-only references.
import { canActAs } from '@jeket-cloud/types/auth';
import type { Role } from '@jeket-cloud/types/auth';
import { useUserRoles } from '@/contexts/AuthContext';

export interface RoleGateProps {
  /** The minimum role required to see the children. */
  role: Role;
  /** What the gate guards. */
  children: ReactNode;
  /** Optional content to render in place of the children when blocked. */
  fallback?: ReactNode;
  /**
   * Force explain mode on for this gate regardless of the global flag.
   * Useful for internal tooling pages where the developer always wants
   * to see what's gated.
   */
  explain?: boolean;
}

/**
 * Read the explain-mode flag.
 *
 * Two opt-in mechanisms, in order of precedence:
 *  1. The `explainGates` URL query param (any truthy string except `0`/`false`)
 *     — per-session, no rebuild needed, intended for admin/dev triage
 *     ("why can't I see the Drain button?").
 *  2. `import.meta.env.VITE_EXPLAIN_ROLE_GATES === 'true'`
 *     — build-time, intended for dev/staging bundles that always want
 *     to surface gates.
 *
 * The query-param branch is intentionally lazy: we read `window.location`
 * inside `useMemo` so SSR-style render passes (none today, but cheap to
 * future-proof) don't reach for `window`.
 */
function useExplainGatesFlag(): boolean {
  return useMemo(() => {
    if (typeof window !== 'undefined') {
      const param = new URLSearchParams(window.location.search).get('explainGates');
      if (param !== null) {
        return param !== '0' && param.toLowerCase() !== 'false';
      }
    }
    // Vite injects import.meta.env at build time; guard for non-Vite test
    // setups even though the orchestrator frontend is Vite-only today.
    try {
      return import.meta.env?.VITE_EXPLAIN_ROLE_GATES === 'true';
    } catch {
      return false;
    }
  }, []);
}

/**
 * Tooltip-on-hover stub rendered in explain mode when the gate would
 * otherwise hide its children. Visual styling matches the rest of the
 * orchestrator UI (Tailwind, dashed outline so it's obviously a stub).
 *
 * Kept inline — extracting to a separate file would invite drift in what
 * is a single-purpose, dev-only affordance.
 */
function ExplainStub({ role }: { role: Role }) {
  const message = `Role-gated: requires "${role}" or higher`;
  return (
    <span
      role="note"
      aria-label={message}
      title={message}
      data-testid="role-gate-explain"
      className="inline-flex items-center gap-1 rounded border border-dashed border-amber-400 bg-amber-50 px-2 py-0.5 text-xs text-amber-800"
    >
      <span aria-hidden="true">[gated:</span>
      <span>{role}</span>
      <span aria-hidden="true">]</span>
    </span>
  );
}

export function RoleGate({ role, children, fallback, explain }: RoleGateProps) {
  const roles = useUserRoles();
  const globalExplain = useExplainGatesFlag();
  const allowed = canActAs(roles, role);

  if (allowed) {
    return <>{children}</>;
  }

  // Explain mode: surface the gate to dev/admin viewers instead of
  // silently dropping it. The per-instance `explain` prop wins over the
  // global flag so a page can opt in even when the URL param is absent.
  if (explain || globalExplain) {
    return <ExplainStub role={role} />;
  }

  if (fallback !== undefined) {
    return <>{fallback}</>;
  }

  return null;
}

export default RoleGate;
